import { MouseEvent, PropsWithChildren, ReactNode, useState } from 'react'
import {
    AppBar,
    Box,
    Button,
    Container,
    Menu,
    MenuItem,
    SxProps,
    Toolbar,
    useMediaQuery,
    useTheme
} from '@mui/material'
import { useUnreadMessages } from 'src/hooks/useUnreadMessages'
import { useYourCareDot } from 'src/hooks/useYourCareDot'

import { TiaLogo } from 'src/components/MUIBased/TiaLogo'
import {
    HeaderBookAppointmentButton,
    HeaderChatButton,
    HeaderYourCareButton
} from 'src/components/MUIBased/buttons'
import { IndicatorIcon } from 'src/components/MUIBased/icons'
import MenuIcon from 'src/assets/icon-menu.svg' // this is bad and affects bundle size
import { BackButton } from 'src/components/MUIBased/buttons'

export const MobileActions = () => {
    const theme = useTheme()
    const buttonColor = theme.palette.text.primary

    const { value: showChatIndicator } = useUnreadMessages()
    const showCareIndicator = useYourCareDot()

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)
    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <>
            <Button
                onClick={handleClick}
                sx={{
                    position: 'absolute',
                    right: '24px',
                    display: 'block',
                    minWidth: '32px',
                    width: '32px',
                    height: '32px',
                    p: '2px 4px 4px 4px !important',
                    ml: 3
                }}
            >
                <img src={MenuIcon} alt="menu" />
                {(showCareIndicator || showChatIndicator) && (
                    <IndicatorIcon sx={{ top: '-7px', right: '1px' }} />
                )}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                sx={{
                    ml: '4px',
                    mt: '28px'
                }}
            >
                <MenuItem onClick={handleClose}>
                    <HeaderYourCareButton
                        color={buttonColor}
                        showIndicator={showCareIndicator}
                    />
                </MenuItem>
                <MenuItem onClick={handleClose}>
                    <HeaderChatButton
                        color={buttonColor}
                        showIndicator={showChatIndicator}
                    />
                </MenuItem>
            </Menu>
        </>
    )
}

interface PageHeaderProps {
    sx?: SxProps
    /**
     * placeholder for rendering actions on the right side of the header on desktop (not visible on mobile)
     */
    desktopActions?: ReactNode
    /**
     * placeholder for rendering an element on the left side of the header on mobile (not visible on desktop)
     */
    mobileBack?: ReactNode
    /**
     * placeholder for rendering an element on the right side of the header on mobile (not visible on desktop)
     */
    mobileActions?: ReactNode
    /**
     * whether to show the appointment booking button, will collide with mobileBack as there is too little space
     * to render a mobile back button, Tia logo, hamburger menu and the appointment booking button.  If a mobile
     * back is defined, this boolean will be ignored
     */
    showAppointmentBookingButton?: boolean
}

const defaultPageHeaderStyle: SxProps = {
    py: { xs: '18px', lg: '24px' }
}

export const PageHeader = (props: PropsWithChildren<PageHeaderProps>) => {
    const theme = useTheme()
    const buttonColor = theme.palette.primary.contrastText // default to desktop white button color
    const mobile = useMediaQuery(theme.breakpoints.down('sm'))

    const { value: showChatIndicator } = useUnreadMessages()
    const showCareIndicator = useYourCareDot()

    const showMobileBackButton = props.mobileBack && mobile
    const showAppointmentBookingButton =
        props.showAppointmentBookingButton && !showMobileBackButton

    return (
        <AppBar position="static">
            <Toolbar sx={{ ...defaultPageHeaderStyle, ...props.sx }}>
                <Container
                    sx={{
                        display: 'flex',
                        justifyContent: showMobileBackButton
                            ? 'center'
                            : 'inherit'
                    }}
                >
                    {showMobileBackButton && (
                        <Box
                            sx={{
                                position: 'absolute',
                                left: '16px',
                                top: '8px'
                            }}
                        >
                            {props.mobileBack}
                        </Box>
                    )}

                    <TiaLogo />

                    <Box
                        sx={{
                            gap: {
                                md: 1,
                                lg: 4
                            },
                            justifyContent: 'flex-end',
                            display: mobile ? 'none' : 'flex',
                            flexGrow: showMobileBackButton ? 0 : 1
                        }}
                    >
                        <HeaderYourCareButton
                            color={buttonColor}
                            showIndicator={showCareIndicator}
                        />
                        <HeaderChatButton
                            color={buttonColor}
                            showIndicator={showChatIndicator}
                        />
                        {showAppointmentBookingButton && (
                            <HeaderBookAppointmentButton />
                        )}
                    </Box>
                    {mobile && props.mobileActions}
                </Container>
            </Toolbar>
        </AppBar>
    )
}

interface StandardPageHeaderProps {
    showMobileBackButton?: boolean
    showAppointmentBookingButton?: boolean
}

export const StandardPageHeader = ({
    showMobileBackButton = true,
    showAppointmentBookingButton = false
}: StandardPageHeaderProps) => {
    return (
        <PageHeader
            mobileBack={
                showMobileBackButton ? (
                    <BackButton
                        variant="white"
                        sx={{ fontSize: '12px !important' }}
                    />
                ) : null
            }
            mobileActions={<MobileActions />}
            showAppointmentBookingButton={showAppointmentBookingButton}
        />
    )
}
